import {alpha, createTheme, Theme} from "@mui/material/styles";
import {
    compositeColor,
    SigningControlColors,
    SigningPanelColors,
    SigningShape,
    contrastColor,
    DEFAULT_PANEL_COLOR,
    foregroundOn,
    panelColors,
    readableColor
} from "./signingColors";
import {signingFonts, SERIF_STACK} from "./signingFonts";
import {DigitalSigningCornerStyle, SnapshotTheme} from "./digitalSigning.types";

export const SIGNING_PAPER = '#F4F1EA';
export const SIGNING_SURFACE = '#FFFFFF';
const SURFACE = SIGNING_SURFACE;
export const SIGNING_INK = '#23201C';
const INK = SIGNING_INK;
const INK_SECONDARY = '#665F55';

const CORNERS: Record<DigitalSigningCornerStyle, SigningShape> = {
    SHARP: {control: 2, card: 2},
    ROUNDED: {control: 10, card: 16},
    PILL: {control: 999, card: 24},
};

const PHONE = '@media (max-width:599.95px)';

export const controlRadius = (theme: Theme): number => (theme.signingShape ?? CORNERS.ROUNDED).control;

export const cardRadius = (theme: Theme): number => (theme.signingShape ?? CORNERS.ROUNDED).card;

export const displayFontFamily = (theme: Theme): string => String(theme.typography.h1.fontFamily ?? SERIF_STACK);

/**
 * The panel's colors: Hero's band, Background's veil - the design's panel color, else white on Hero and the paper of
 * Paper on Background. The page's theme and the settings' crop preview both take them from here.
 */
export const panelColorsOf = (snapshotTheme?: SnapshotTheme): SigningPanelColors => panelColors(
    readableColor(snapshotTheme?.panelColor) ?? (snapshotTheme?.variant === 'BACKGROUND' ? SIGNING_PAPER : DEFAULT_PANEL_COLOR)
);

export const signingThemeToMuiTheme = (snapshotTheme: SnapshotTheme | undefined): Theme => {
    const backgrounds = [SIGNING_PAPER, SURFACE];
    const primary = readableColor(snapshotTheme?.primaryColor) ?? INK;
    const fill = compositeColor(contrastColor(primary, backgrounds, 3.1), SIGNING_PAPER);
    const controls: SigningControlColors = {
        border: contrastColor(alpha(INK, 0.3), backgrounds, 3.05),
        hoverBorder: contrastColor(alpha(INK, 0.6), backgrounds, 3.5),
        accent: contrastColor(primary, backgrounds, 4.55),
        focus: contrastColor(primary, backgrounds, 3.1),
        fill,
        onFill: foregroundOn(fill),
    };
    const error = contrastColor('#B3261E', backgrounds, 4.55);
    const focusSx = {outline: `2px solid ${controls.focus}`, outlineOffset: 3};
    const shape = CORNERS[snapshotTheme?.cornerStyle ?? 'ROUNDED'] ?? CORNERS.ROUNDED;
    const fonts = signingFonts(snapshotTheme?.fontFamily);
    const body = fonts.body;
    const display = fonts.heading;
    const font = !fonts.classic;
    const displayWeight = font ? 600 : 400;

    return createTheme({
        signingControls: controls,
        signingShape: shape,
        signingPanel: panelColorsOf(snapshotTheme),
        palette: {
            mode: 'light',
            primary: {main: primary, contrastText: foregroundOn(compositeColor(primary, SIGNING_PAPER))},
            error: {main: error},
            background: {default: SIGNING_PAPER, paper: SURFACE},
            text: {primary: INK, secondary: INK_SECONDARY, disabled: alpha(INK, 0.45)},
            divider: alpha(INK, 0.14),
            action: {
                active: INK_SECONDARY,
                hover: alpha(INK, 0.05),
                selected: alpha(primary, 0.12),
                focus: alpha(INK, 0.12),
                disabled: alpha(INK, 0.45),
                disabledBackground: alpha(INK, 0.12),
            },
        },
        typography: {
            fontFamily: body,
            h1: {fontFamily: display, fontSize: font ? 34 : 38, fontWeight: displayWeight, lineHeight: 1.12, letterSpacing: '-0.01em', [PHONE]: {fontSize: font ? 29 : 32}},
            h2: {fontFamily: display, fontSize: font ? 24 : 26, fontWeight: displayWeight, lineHeight: 1.2, [PHONE]: {fontSize: font ? 21 : 23}},
            h3: {fontFamily: display, fontSize: font ? 22 : 24, fontWeight: displayWeight, lineHeight: 1.25},
            subtitle1: {fontSize: 16, fontWeight: 600, lineHeight: 1.4},
            body1: {fontSize: 16, lineHeight: 1.5},
            body2: {fontSize: 14, lineHeight: 1.45},
            caption: {fontSize: 13, lineHeight: 1.4},
            button: {fontSize: 16, fontWeight: 600, textTransform: 'none', letterSpacing: 0},
        },
        shape: {borderRadius: Math.min(shape.control, 24)},
        components: {
            MuiPaper: {
                styleOverrides: {root: {backgroundImage: 'none', colorScheme: 'light'}},
            },
            MuiButtonBase: {
                defaultProps: {disableRipple: true},
                styleOverrides: {root: {fontFamily: body}},
            },
            MuiButton: {
                defaultProps: {disableElevation: true},
                styleOverrides: {
                    root: ({ownerState}) => ({
                        '&.Mui-focusVisible': focusSx,
                        ...((ownerState.variant === 'outlined' || ownerState.variant === 'text') && {
                            color: controls.accent,
                            ...(ownerState.variant === 'outlined' && {borderColor: controls.border}),
                            '&:hover': {borderColor: controls.hoverBorder, backgroundColor: alpha(controls.accent, 0.08)},
                        }),
                        '&.Mui-disabled': {
                            color: alpha(INK, 0.45),
                            ...(ownerState.variant === 'outlined' && {borderColor: alpha(INK, 0.2)}),
                        },
                    }),
                },
            },
            MuiIconButton: {
                styleOverrides: {root: {'&.Mui-focusVisible': focusSx}},
            },
        },
    });
};
